import React, { useState, useEffect } from 'react';
import { questionnaireService } from '../services/questionnaireService';
import { schoolService } from '../services/schoolService';
import { classService } from '../services/classService';
import { exportService } from '../services/exportService';
import { exportLogService, ExportLog } from '../services/exportLogService';
import { Questionnaire, School } from '../types';
import { DownloadIcon } from '../components/Icons';
import { useToast } from '../context/ToastContext';

const DataExportPage: React.FC = () => {
  const { showToast } = useToast();
  const [schools, setSchools] = useState<School[]>([]);
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);
  const [academicYears, setAcademicYears] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Filter state
  const [selectedAcademicYear, setSelectedAcademicYear] = useState<string>('');
  const [selectedSchoolIds, setSelectedSchoolIds] = useState<number[]>([]);
  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState<number | null>(null);
  const [exportMode, setExportMode] = useState<'ANSWER_TEXT' | 'SCORE'>('ANSWER_TEXT');

  // 导出历史
  const [exportLogs, setExportLogs] = useState<ExportLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);

  useEffect(() => {
    loadData();
    refreshLogs();
  }, []);

  const refreshLogs = async () => {
    try {
      setLogsLoading(true);
      const page = await exportLogService.list(0, 50);
      setExportLogs(page.content);
    } catch (e) {
      console.error('Failed to load export logs:', e);
    } finally {
      setLogsLoading(false);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [schoolData, questionnaireData, yearsData] = await Promise.all([
        schoolService.getAllSchoolsSimple(),
        questionnaireService.getQuestionnaires({ page: 0, size: 1000 }),
        classService.getAllAcademicYears(),
      ]);
      setSchools(schoolData);
      setQuestionnaires(questionnaireData.content);
      setAcademicYears(yearsData.sort().reverse());
    } catch (error) {
      console.error('Failed to load data:', error);
      showToast('加载数据失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 不再按 q.academicYear 精确匹配过滤问卷下拉 — 之前的实现会把所有
  // academicYear 字段不等于"2026"的问卷全部隐藏，导致 demo 选 2026 看不到任何问卷。
  // 用户的意图是"2026 的所有班级"，这跟问卷自身的 academicYear 字段是两个维度。
  // 学年选择保留作为导出范围意向标记；导出时由后端按 class enrollment 实际筛选。
  const filteredQuestionnaires = questionnaires;

  const toggleSchool = (schoolId: number) => {
    setSelectedSchoolIds((prev) =>
      prev.includes(schoolId) ? prev.filter((id) => id !== schoolId) : [...prev, schoolId]
    );
  };

  const selectAllSchools = () => {
    if (selectedSchoolIds.length === schools.length) {
      setSelectedSchoolIds([]);
    } else {
      setSelectedSchoolIds(schools.map((s) => s.id));
    }
  };

  const handleExport = async () => {
    if (!selectedQuestionnaireId) {
      showToast('请选择要导出的问卷', 'warning');
      return;
    }

    try {
      setExporting(true);
      await exportService.exportQuestionnaireResults({
        questionnaireId: selectedQuestionnaireId,
        schoolIds: selectedSchoolIds.length > 0 ? selectedSchoolIds : undefined,
        mode: exportMode,
        academicYear: selectedAcademicYear || undefined,
      });
      showToast('导出成功', 'success');
    } catch (error) {
      console.error('Export failed:', error);
      showToast('导出失败，请重试', 'error');
    } finally {
      setExporting(false);
      refreshLogs();
    }
  };

  const formatFileSize = (bytes: number | null): string => {
    if (bytes == null) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  const formatDuration = (ms: number | null): string => {
    if (ms == null) return '-';
    if (ms < 1000) return `${ms} ms`;
    return `${(ms / 1000).toFixed(1)} s`;
  };

  const formatTime = (iso: string): string => {
    try {
      const d = new Date(iso);
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    } catch { return iso; }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold font-headline text-on-surface mb-2">数据导出</h1>
        <p className="font-body text-on-surface-variant">导出问卷结果数据为 Excel 文件</p>
      </div>

      <div className="bg-surface-container-lowest rounded-3xl shadow-soft p-6 space-y-6">
        {/* Filter Section */}
        <div>
          <h2 className="text-lg font-semibold font-headline text-on-surface mb-4">筛选条件</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Academic Year */}
            <div>
              <label className="block text-sm font-medium font-label text-on-surface mb-2">学年</label>
              <select
                value={selectedAcademicYear}
                onChange={(e) => setSelectedAcademicYear(e.target.value)}
                className="w-full px-3 py-2 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
              >
                <option value="">全部学年</option>
                {academicYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              <p className="text-xs text-on-surface-variant font-body mt-1">
                按学生班级学年过滤答卷（与"推送"时学年含义一致）
              </p>
            </div>

            {/* Schools (multi-select checkboxes) */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium font-label text-on-surface">学校</label>
                <button
                  onClick={selectAllSchools}
                  className="text-xs font-label text-primary-600 hover:text-primary-700"
                >
                  {selectedSchoolIds.length === schools.length ? '取消全选' : '全选'}
                </button>
              </div>
              <div className="bg-surface-container-high rounded-xl p-3 max-h-40 overflow-y-auto space-y-2">
                {schools.length === 0 ? (
                  <p className="text-sm font-body text-on-surface-variant">暂无学校</p>
                ) : (
                  schools.map((school) => (
                    <label key={school.id} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedSchoolIds.includes(school.id)}
                        onChange={() => toggleSchool(school.id)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mr-2"
                      />
                      <span className="text-sm font-body text-on-surface">{school.name}</span>
                    </label>
                  ))
                )}
              </div>
              {selectedSchoolIds.length > 0 && (
                <p className="text-xs font-label text-on-surface-variant mt-1">
                  已选择 {selectedSchoolIds.length} 所学校
                </p>
              )}
            </div>
          </div>

          {/* Questionnaire Selection */}
          <div className="mt-4">
            <label className="block text-sm font-medium font-label text-on-surface mb-2">选择问卷</label>
            <select
              value={selectedQuestionnaireId ?? ''}
              onChange={(e) =>
                setSelectedQuestionnaireId(e.target.value ? Number(e.target.value) : null)
              }
              className="w-full px-3 py-2 bg-surface-container-high rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-200"
            >
              <option value="">请选择问卷</option>
              {filteredQuestionnaires.map((q) => (
                <option key={q.id} value={q.id}>
                  {q.title}
                  {q.version ? ` ${q.version}` : ''}
                  {` - ${q.statusDisplayName}`}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Export Mode */}
        <div className="border-t border-surface-container-high pt-6">
          <h2 className="text-lg font-semibold font-headline text-on-surface mb-4">导出格式</h2>
          <div className="flex items-center gap-6">
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="exportMode"
                value="ANSWER_TEXT"
                checked={exportMode === 'ANSWER_TEXT'}
                onChange={() => setExportMode('ANSWER_TEXT')}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 mr-2"
              />
              <span className="text-sm font-body text-on-surface">答案文字</span>
              <span className="text-xs font-label text-on-surface-variant ml-2">（题目列显示可读文本）</span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="exportMode"
                value="SCORE"
                checked={exportMode === 'SCORE'}
                onChange={() => setExportMode('SCORE')}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 mr-2"
              />
              <span className="text-sm font-body text-on-surface">分数</span>
              <span className="text-xs font-label text-on-surface-variant ml-2">（题目列显示选项得分）</span>
            </label>
          </div>
        </div>

        {/* Export Button */}
        <div className="border-t border-surface-container-high pt-6 flex justify-center">
          <button
            onClick={handleExport}
            disabled={!selectedQuestionnaireId || exporting}
            className="inline-flex items-center gap-2 px-6 py-3 gradient-primary text-on-primary shadow-teal rounded-xl hover:shadow-soft-md transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {exporting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                导出中...
              </>
            ) : (
              <>
                <DownloadIcon size={20} />
                导出 Excel
              </>
            )}
          </button>
        </div>
      </div>

      {/* 导出历史 */}
      <div className="bg-surface-container-lowest rounded-3xl shadow-soft p-6 mt-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-on-surface font-headline">导出历史</h2>
            <p className="text-xs text-on-surface-variant font-body mt-1">
              最近 50 条 admin 导出记录（所有管理员可见）
            </p>
          </div>
          <button
            onClick={refreshLogs}
            className="text-sm text-primary-600 hover:text-primary-700 font-label"
            disabled={logsLoading}
          >
            {logsLoading ? '刷新中...' : '刷新'}
          </button>
        </div>

        {exportLogs.length === 0 ? (
          <div className="text-center text-on-surface-variant py-8 font-body">
            {logsLoading ? '加载中...' : '暂无导出记录'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-surface-container-low">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-on-surface-variant font-label">时间</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-on-surface-variant font-label">操作人</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-on-surface-variant font-label">问卷</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-on-surface-variant font-label">学校范围</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-on-surface-variant font-label">模式</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-on-surface-variant font-label">学年</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-on-surface-variant font-label">行数</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-on-surface-variant font-label">大小</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-on-surface-variant font-label">耗时</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-on-surface-variant font-label">状态</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-container-high">
                {exportLogs.map((l) => (
                  <tr key={l.id} className="hover:bg-surface-container-low">
                    <td className="px-3 py-2 whitespace-nowrap text-on-surface-variant font-body">{formatTime(l.createdAt)}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-on-surface font-body">{l.userName || '-'}</td>
                    <td className="px-3 py-2 text-on-surface font-body max-w-[240px] truncate" title={l.questionnaireTitle || ''}>{l.questionnaireTitle || '-'}</td>
                    <td className="px-3 py-2 text-on-surface-variant font-body max-w-[200px] truncate" title={l.schoolNames || ''}>{l.schoolNames || '全部学校'}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-on-surface-variant font-body">{l.mode === 'SCORE' ? '分数' : l.mode === 'ANSWER_TEXT' ? '答案文字' : (l.mode || '-')}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-on-surface-variant font-body">{l.academicYear || '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-right text-on-surface font-body">{l.rowCount != null ? l.rowCount.toLocaleString() : '-'}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-right text-on-surface-variant font-body">{formatFileSize(l.fileSizeBytes)}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-right text-on-surface-variant font-body">{formatDuration(l.durationMs)}</td>
                    <td className="px-3 py-2 whitespace-nowrap font-body">
                      {l.status === 'SUCCESS' ? (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">成功</span>
                      ) : l.status === 'FAILED' ? (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700" title={l.errorMessage || ''}>失败</span>
                      ) : (
                        <span className="text-on-surface-variant">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataExportPage;
